import Seo from "@/components/Seo";
import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PhotoUpload from "@/components/PhotoUpload";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  Info,
  Shield,
  Clock,
  Wrench,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lazy load the interactive map (Leaflet) to keep the initial bundle light.
const SaintLuciaMap = lazy(() => import("@/components/SaintLuciaMap"));

const SERVICE_OPTIONS = [
  { value: "plumbing", label: "Plumbing" },
  { value: "electrical", label: "Electrical" },
  { value: "roofing", label: "Roofing" },
  { value: "tiling", label: "Tiling" },
  { value: "carpentry", label: "Carpentry" },
  { value: "painting", label: "Painting" },
  { value: "remodeling", label: "Remodeling" },
  { value: "cleaning", label: "Cleaning" },
  { value: "lawn_care", label: "Lawn Care" },
  { value: "hvac", label: "HVAC" },
  { value: "pest_control", label: "Pest Control" },
  { value: "general_care", label: "General Care" },
  { value: "metalwork", label: "Metalwork & Welding" },
  { value: "other", label: "Other" },
];

const BUDGET_OPTIONS = [
  "Under XCD $500",
  "XCD $500 – $1,500",
  "XCD $1,500 – $5,000",
  "XCD $5,000 – $15,000",
  "XCD $15,000+",
  "Not sure",
];

const DISCLAIMER =
  "Free quotes are based on information provided by customers and are subject to change upon professional viewing.";

// Pre-fill presets for the homepage Move-In / Move-Out package adverts.
const PACKAGE_PREFILLS = {
  "move-in": {
    service_category: "other",
    details:
      "I'm interested in the Move-In Package. Please prepare a quote covering: deep cleaning, furniture assembly, TV & wall/shelf mounting, and minor repairs & touch-ups.",
  },
  "move-out": {
    service_category: "other",
    details:
      "I'm interested in the Move-Out Package. Please prepare a quote covering: move-out deep cleaning, furniture disassembly & moving help, and patch & paint touch-ups.",
  },
};

export default function QuotePage() {
  const [searchParams] = useSearchParams();
  const packagePreset = PACKAGE_PREFILLS[searchParams.get("package")] || {};
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_category: packagePreset.service_category || "",
    customer_type: "residential",
    island: "Saint Lucia",
    community: "",
    address: "",
    preferred_date: "",
    budget_range: "",
    details: packagePreset.details || "",
    pinned_location: null,
  });

  useEffect(() => {
    axios
      .get(`${API}/locations`)
      .then((r) => setServiceAreas(r.data?.areas || []))
      .catch(() => {});
  }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const communitiesForIsland = (() => {
    const a = serviceAreas.find((x) => x.island === form.island);
    return a ? a.communities : [];
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_category || !form.details) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quotes`, form);
      const newQuoteId = res.data?.id;
      const uploadToken = res.data?.upload_token;

      // Upload photos best-effort using the one-time upload token
      if (newQuoteId && uploadToken && photos.length > 0) {
        try {
          const fd = new FormData();
          photos.forEach((f) => fd.append("photos", f));
          fd.append("upload_token", uploadToken);
          await axios.post(`${API}/quotes/${newQuoteId}/photos`, fd, {
            headers: {
              "Content-Type": "multipart/form-data",
              "X-Upload-Token": uploadToken,
            },
          });
        } catch (uploadErr) {
          console.error("Quote photo upload failed:", uploadErr);
        }
      }

      setSubmitted(true);
      toast.success("Quote request sent! We'll be in touch soon.");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to submit quote request");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[70vh] bg-slate-50 flex items-center" data-testid="quote-success-screen">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-9 h-9 text-green-600" />
          </div>
          <h1
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Thanks — your quote request is in.
          </h1>
          <p className="text-slate-600 mb-2">
            A ProToCall team member will reach out within one business day.
          </p>
          <p className="text-xs text-slate-500 max-w-md mx-auto mb-8">
            {DISCLAIMER}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="bg-orange-500 hover:bg-orange-600 text-white">
              <Link to="/services">Browse Services</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/">Back to Home</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quote-page">
      <Seo
        title="Get a Free Quote"
        description="Request a free, no-obligation quote for any handyman service across Saint Lucia and the Caribbean. Fast 24-hour response from ProToCall."
        path="/quote"
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-orange-500/15 text-orange-400 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              No obligation • Fast response
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
              data-testid="quote-hero-title"
            >
              Get a <span className="text-orange-500">Free Quote</span>
            </h1>
            <p className="text-lg text-slate-300 max-w-lg mb-8">
              Tell us about your project and we'll get back to you within one business day
              with an estimate tailored to your needs.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Clock, label: "24h response" },
                { icon: Shield, label: "Licensed & insured" },
                { icon: Wrench, label: "13+ services" },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-2 text-slate-300">
                  <b.icon className="w-4 h-4 text-orange-400" />
                  <span className="text-sm">{b.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Disclaimer card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6"
            data-testid="quote-disclaimer-box"
          >
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-orange-400 flex-shrink-0 mt-1" />
              <div>
                <p className="text-white font-semibold mb-2">How quotes work</p>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {DISCLAIMER} A site visit may be required to confirm the final price for
                  larger or more complex jobs.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-6 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                {/* Customer type */}
                <div>
                  <Label className="text-slate-900 font-medium mb-3 block">
                    I'm requesting for...
                  </Label>
                  <RadioGroup
                    value={form.customer_type}
                    onValueChange={(v) => update("customer_type", v)}
                    className="flex gap-3"
                  >
                    <Label
                      htmlFor="qt-res"
                      className={`flex-1 cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                        form.customer_type === "residential"
                          ? "border-orange-500 bg-orange-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <RadioGroupItem id="qt-res" value="residential" className="sr-only" />
                      <p className="font-semibold text-slate-900">Residential</p>
                      <p className="text-xs text-slate-500">My home</p>
                    </Label>
                    <Label
                      htmlFor="qt-com"
                      className={`flex-1 cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                        form.customer_type === "commercial"
                          ? "border-orange-500 bg-orange-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <RadioGroupItem id="qt-com" value="commercial" className="sr-only" />
                      <p className="font-semibold text-slate-900">Commercial</p>
                      <p className="text-xs text-slate-500">My business</p>
                    </Label>
                  </RadioGroup>
                </div>

                {/* Name + Phone */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="qt-name" className="text-slate-900 font-medium">
                      Full name *
                    </Label>
                    <Input
                      id="qt-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Jane Doe"
                      required
                      data-testid="quote-input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="qt-phone" className="text-slate-900 font-medium">
                      Phone *
                    </Label>
                    <Input
                      id="qt-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="+1 758 ..."
                      required
                      data-testid="quote-input-phone"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="qt-email" className="text-slate-900 font-medium">
                    Email *
                  </Label>
                  <Input
                    id="qt-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="quote-input-email"
                  />
                </div>

                {/* Service */}
                <div>
                  <Label className="text-slate-900 font-medium">Service needed *</Label>
                  <Select
                    value={form.service_category}
                    onValueChange={(v) => update("service_category", v)}
                  >
                    <SelectTrigger data-testid="quote-select-service">
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Location */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-900 font-medium">Island</Label>
                    <Select
                      value={form.island}
                      onValueChange={(v) => {
                        update("island", v);
                        update("community", "");
                      }}
                    >
                      <SelectTrigger data-testid="quote-select-island">
                        <SelectValue placeholder="Pick an island" />
                      </SelectTrigger>
                      <SelectContent>
                        {(serviceAreas.length > 0
                          ? serviceAreas
                          : [{ island: "Saint Lucia", is_primary: true }]
                        ).map((a) => (
                          <SelectItem key={a.island} value={a.island}>
                            {a.island}
                            {a.is_primary ? " (Primary)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-900 font-medium">Community / Town</Label>
                    {communitiesForIsland.length > 0 ? (
                      <Select
                        value={form.community}
                        onValueChange={(v) => update("community", v)}
                      >
                        <SelectTrigger data-testid="quote-select-community">
                          <SelectValue placeholder="Pick a community" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[280px]">
                          {communitiesForIsland.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="qt-community"
                        value={form.community}
                        onChange={(e) => update("community", e.target.value)}
                        placeholder="Type your community"
                        data-testid="quote-input-community"
                      />
                    )}
                  </div>
                </div>
                <div className="pt-1">
                  <Suspense
                    fallback={
                      <div className="h-[280px] rounded-lg bg-slate-100 animate-pulse" />
                    }
                  >
                    <SaintLuciaMap
                      island={form.island}
                      initialPosition={form.pinned_location}
                      onLocationSelect={(loc) => update("pinned_location", loc)}
                    />
                  </Suspense>
                </div>
                <div>
                  <Label htmlFor="qt-date" className="text-slate-900 font-medium">
                    Preferred start date
                  </Label>
                  <Input
                    id="qt-date"
                    type="date"
                    value={form.preferred_date}
                    onChange={(e) => update("preferred_date", e.target.value)}
                    data-testid="quote-input-date"
                  />
                </div>

                {/* Address */}
                <div>
                  <Label htmlFor="qt-address" className="text-slate-900 font-medium">
                    Project address (optional)
                  </Label>
                  <Input
                    id="qt-address"
                    value={form.address}
                    onChange={(e) => update("address", e.target.value)}
                    placeholder="Street, neighbourhood"
                    data-testid="quote-input-address"
                  />
                </div>

                {/* Budget */}
                <div>
                  <Label className="text-slate-900 font-medium">Approx. budget</Label>
                  <Select
                    value={form.budget_range}
                    onValueChange={(v) => update("budget_range", v)}
                  >
                    <SelectTrigger data-testid="quote-select-budget">
                      <SelectValue placeholder="Pick a range (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUDGET_OPTIONS.map((b) => (
                        <SelectItem key={b} value={b}>
                          {b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Details */}
                <div>
                  <Label htmlFor="qt-details" className="text-slate-900 font-medium">
                    Tell us about the work *
                  </Label>
                  <Textarea
                    id="qt-details"
                    value={form.details}
                    onChange={(e) => update("details", e.target.value)}
                    placeholder="Describe the issue, scope, materials, or any photos you'll share..."
                    rows={5}
                    required
                    data-testid="quote-input-details"
                  />
                </div>

                {/* Photo upload */}
                <PhotoUpload
                  value={photos}
                  onChange={setPhotos}
                  label="Add photos of the job (optional)"
                  helper="A clear photo speeds up your quote. Up to 5 · max 8MB each."
                  testId="quote-photos"
                />

                {/* Disclaimer */}
                <div
                  className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 flex gap-2"
                  data-testid="quote-disclaimer-text"
                >
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>{DISCLAIMER}</p>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 font-semibold"
                  data-testid="quote-submit-btn"
                >
                  {submitting ? "Sending..." : "Get My Free Quote"}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
